import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { COPY, type CopyKey, type Lang } from "../content/landing";

export type { Lang, CopyKey };

const LANG_STORAGE_KEY = "barmaid-landing-lang";

let currentLang: Lang = "pl";

function readStoredLang(): Lang {
  try {
    const value = localStorage.getItem(LANG_STORAGE_KEY);
    if (value === "en" || value === "pl") return value;
  } catch {
    // ignore
  }
  return "pl";
}

export function initLang(): Lang {
  currentLang = readStoredLang();
  document.documentElement.lang = currentLang;
  return currentLang;
}

export function t(key: CopyKey, vars?: Record<string, string | number>): string {
  let text: string = COPY[currentLang][key];
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.replaceAll(`{${name}}`, String(value));
    }
  }
  return text;
}

interface LangContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const LangContext = createContext<LangContextValue | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => initLang());

  const setLang = useCallback((next: Lang) => {
    currentLang = next;
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    document.documentElement.lang = next;
    setLangState(next);
  }, []);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
}

export function useT() {
  const { lang } = useLang();
  return useCallback(
    (key: CopyKey, vars?: Record<string, string | number>) => {
      let text: string = COPY[lang][key];
      if (vars) {
        for (const [name, value] of Object.entries(vars)) {
          text = text.replaceAll(`{${name}}`, String(value));
        }
      }
      return text;
    },
    [lang],
  );
}
